import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
  type Identifier,
} from "ra-core";
import { useCallback, useEffect, useRef, useState } from "react";

import type { CrmDataProvider } from "../providers/types";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { TimeEntry } from "../types";
import {
  getPendingStop,
  isNetworkFailure,
  setPendingStop,
  usePendingStop,
} from "./timerSync";

// Hvor ofte en uavklart stopp sendes på nytt, i tillegg til når nettet
// kommer tilbake.
const RETRY_MS = 15000;

/**
 * Brukerens timer, delt av alle flatene (oppgaveskjemaet, tidspanelet,
 * listene og toppbaren). Start og stopp går gjennom RPC-ene
 * start_time_entry/stop_time_entry (PRD US06):
 * - start stopper en løpende timer i samme transaksjon,
 * - stopp er idempotent; faller nettet ut, står timeren som «Uavklart»
 *   (`unsettled`) til serveren har bekreftet, og stoppen sendes på nytt.
 */
export const useTimer = () => {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { timeBillableByDefault } = useConfigurationContext();
  const pendingStop = usePendingStop();
  const [busy, setBusy] = useState(false);

  const { data: running = [] } = useGetList<TimeEntry>(
    "time_entries",
    {
      filter: { sales_id: identity?.id, "ended_at@is": null },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: identity?.id != null },
  );
  const runningEntry = running[0];

  // Klokka tikker hvert sekund mens en timer går.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!runningEntry) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [runningEntry]);

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ["time_entries"] }),
    [queryClient],
  );

  const notifyError = useCallback(
    (error: unknown) =>
      notify((error as Error)?.message || "ra.notification.http_error", {
        type: "error",
      }),
    [notify],
  );

  // Send en uavklart stopp. true = serveren bekreftet (eller sa nei).
  const sendPendingStop = useCallback(async () => {
    const stop = getPendingStop();
    if (!stop) return true;
    try {
      await dataProvider.stopTimeEntry(stop.entryId, stop.endedAt);
      setPendingStop(null);
      await refresh();
      return true;
    } catch (error) {
      if (isNetworkFailure(error)) return false;
      // Serveren svarte, men nektet: stoppen er ikke lagret. Si det.
      setPendingStop(null);
      notifyError(error);
      await refresh();
      return true;
    }
  }, [dataProvider, refresh, notifyError]);

  const sendRef = useRef(sendPendingStop);
  sendRef.current = sendPendingStop;

  // Prøv igjen når nettet er tilbake, og jevnlig mens noe er uavklart.
  useEffect(() => {
    // Flere komponenter kan ha hver sin løkke; stoppen er idempotent.
    if (!pendingStop) return;
    const tryAgain = () => void sendRef.current();
    window.addEventListener("online", tryAgain);
    const id = window.setInterval(tryAgain, RETRY_MS);
    tryAgain();
    return () => {
      window.removeEventListener("online", tryAgain);
      window.clearInterval(id);
    };
  }, [pendingStop]);

  const stop = useCallback(async () => {
    const entry = runningEntry;
    if (!entry) return;
    // Stopptiden er når brukeren trykket, ikke når serveren svarer.
    setPendingStop({ entryId: entry.id, endedAt: new Date().toISOString() });
    setBusy(true);
    try {
      await sendPendingStop();
    } finally {
      setBusy(false);
    }
  }, [runningEntry, sendPendingStop]);

  const start = useCallback(
    async (workItemId: Identifier) => {
      setBusy(true);
      try {
        // En uavklart stopp må lagres først, ellers ville serveren stoppet
        // timeren med sin egen (senere) tid.
        if (getPendingStop() && !(await sendPendingStop())) {
          notify("crm.time.unsettled_wait", {
            type: "warning",
            messageArgs: {
              _: "Forrige stopp er ikke bekreftet ennå. Prøv igjen når nettet er tilbake.",
            },
          });
          return;
        }
        await dataProvider.startTimeEntry(workItemId, timeBillableByDefault);
        await refresh();
      } catch (error) {
        if (isNetworkFailure(error)) {
          notify("crm.time.start_unsettled", {
            type: "warning",
            messageArgs: {
              _: "Fikk ikke svar fra serveren. Sjekker om timeren startet …",
            },
          });
          window.addEventListener("online", () => void refresh(), {
            once: true,
          });
        } else {
          notifyError(error);
        }
        await refresh();
      } finally {
        setBusy(false);
      }
    },
    [
      dataProvider,
      notify,
      notifyError,
      refresh,
      sendPendingStop,
      timeBillableByDefault,
    ],
  );

  const unsettled =
    pendingStop != null &&
    (runningEntry == null ||
      String(pendingStop.entryId) === String(runningEntry.id));

  return {
    runningEntry: unsettled ? undefined : runningEntry,
    // Stoppen som venter på bekreftelse, og timeren den gjelder (hvis
    // serveren fortsatt ser den som løpende).
    unsettledStop: unsettled ? pendingStop : null,
    unsettledEntry: unsettled ? runningEntry : undefined,
    nowMs,
    busy,
    start,
    stop,
    retry: sendPendingStop,
  };
};
